import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Flame, Download, Share2, X, Trophy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * StreakShareModal — renders a Rastyle-branded streak flex card that can be
 * downloaded as PNG or shared natively via the Web Share API (with file).
 *
 * Props:
 *   open          — boolean controlling visibility
 *   onClose       — close handler
 *   userName      — display name on the card
 *   currentStreak — number (>= 0)
 *   longestStreak — number
 *   plays         — number
 *   badgeCount    — number of unlocked badges
 *   totalBadges   — total badges available
 */
const StreakShareModal = ({
  open,
  onClose,
  userName = 'Selector',
  currentStreak = 0,
  longestStreak = 0,
  plays = 0,
  badgeCount = 0,
  totalBadges = 9,
}) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const renderCanvas = async () => {
    if (!cardRef.current) return null;
    return await html2canvas(cardRef.current, {
      backgroundColor: '#0a0a0a',
      scale: 2,
      useCORS: true,
      logging: false,
    });
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const canvas = await renderCanvas();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `rastyle-streak-${currentStreak}days.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Streak card downloaded!');
    } catch (e) {
      toast.error('Could not generate card. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const canvas = await renderCanvas();
      if (!canvas) return;
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('Blob failed');
      const file = new File([blob], `rastyle-streak-${currentStreak}days.png`, { type: 'image/png' });

      const shareData = {
        title: `${currentStreak}-day streak on Rastyle Kenya`,
        text: `🔥 ${currentStreak}-day Gaming Room streak on Rastyle Kenya. Catch me in the leaderboard. rastyle.co.ke`,
        files: [file],
      };

      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: download
        const link = document.createElement('a');
        link.download = `rastyle-streak-${currentStreak}days.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.info('Share not supported — saved to downloads instead.');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        toast.error('Could not share. Try Download instead.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="streak-share-modal"
    >
      <div
        className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-md my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-heading font-extrabold text-lg uppercase tracking-tight flex items-center gap-2">
            <Share2 className="w-5 h-5 text-rastyle-orange" /> Share Your Streak
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
            data-testid="streak-share-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* The shareable card — html2canvas rasterises THIS element */}
        <div className="p-5">
          <div
            ref={cardRef}
            className="relative w-full aspect-square rounded-2xl overflow-hidden"
            style={{
              background:
                'radial-gradient(circle at top right, rgba(255,116,0,0.35), transparent 55%), radial-gradient(circle at bottom left, rgba(255,107,53,0.25), transparent 50%), #0a0a0a',
            }}
            data-testid="streak-share-card"
          >
            {/* Top brand strip */}
            <div className="absolute top-0 inset-x-0 px-6 pt-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rastyle-orange to-red-600 flex items-center justify-center">
                  <span className="font-heading font-black text-white text-sm leading-none">RR</span>
                </div>
                <div className="leading-tight">
                  <p className="font-heading font-extrabold text-white text-sm uppercase tracking-tight">Rastyle Kenya</p>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono">Gaming Room</p>
                </div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">rastyle.co.ke</span>
            </div>

            {/* Center — Streak number */}
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <div className="flex items-center gap-3 mb-2">
                <Flame className="w-12 h-12 text-rastyle-orange drop-shadow-[0_0_20px_rgba(255,116,0,0.6)]" />
                <div className="text-left">
                  <p className="font-heading font-black text-7xl text-white leading-none">{currentStreak}</p>
                  <p className="font-mono text-xs uppercase tracking-widest text-rastyle-orange mt-1">
                    {currentStreak === 1 ? 'day streak' : 'day streak'}
                  </p>
                </div>
              </div>
              <p className="font-heading font-extrabold text-2xl text-white uppercase tracking-tight mt-4">
                {userName}
              </p>
              <p className="text-zinc-400 text-xs mt-1 max-w-[80%]">
                {currentStreak >= 7
                  ? 'Proper selector status. Sound system tested. 🇰🇪'
                  : currentStreak >= 3
                  ? 'On fire — climbing the Nairobi leaderboard.'
                  : 'Just getting started — selector in the making.'}
              </p>
            </div>

            {/* Bottom stats strip */}
            <div className="absolute bottom-0 inset-x-0 px-6 pb-6">
              <div className="grid grid-cols-3 gap-2 bg-black/50 backdrop-blur-sm rounded-xl border border-white/10 p-3">
                <div className="text-center border-r border-white/10">
                  <p className="font-mono font-extrabold text-white text-lg leading-none">{longestStreak}</p>
                  <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">Best Streak</p>
                </div>
                <div className="text-center border-r border-white/10">
                  <p className="font-mono font-extrabold text-rastyle-orange text-lg leading-none">{plays}</p>
                  <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">Total Plays</p>
                </div>
                <div className="text-center">
                  <p className="font-mono font-extrabold text-fomo-amber text-lg leading-none">
                    {badgeCount}<span className="text-zinc-600 text-sm">/{totalBadges}</span>
                  </p>
                  <p className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">Badges</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3 mt-5">
            <button
              onClick={handleDownload}
              disabled={busy}
              className="h-12 bg-white/5 border border-white/10 hover:border-rastyle-orange/50 hover:bg-white/10 rounded-full text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              data-testid="streak-share-download-btn"
            >
              <Download className="w-4 h-4" /> Download
            </button>
            <button
              onClick={handleShare}
              disabled={busy}
              className="h-12 bg-rastyle-orange text-black font-bold uppercase tracking-wide rounded-full flex items-center justify-center gap-2 hover:bg-orange-400 transition-colors disabled:opacity-50"
              data-testid="streak-share-share-btn"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
          <p className="text-center text-[11px] text-zinc-500 mt-3">
            <Sparkles className="w-3 h-3 inline mr-1 text-rastyle-orange" />
            Card includes Rastyle branding so friends can find the Gaming Room.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StreakShareModal;
